import {
  Grid,
  GridItem,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Select,
  Input,
  SimpleGrid,
} from '@chakra-ui/react';

import SectionCard from '../../components/SectionCard';
import ValidatedField from '../../components/ValidatedField';
import { useAnswerCodes, useValidationSchema } from '../../hooks';
import type { VehicleComponentProps } from './types';

const ALLOWED_VEHICLE_USE_LABELS = ['pleasure', 'commuting', 'business'];

const VehicleUseDistanceValue = ({
  vehicle,
  index,
  onVehicleChange,
}: VehicleComponentProps) => {
  const { getOptions, getCode, getLabel } = useAnswerCodes();
  const { isFieldRequired, validateField } = useValidationSchema();

  const vehicleUseOptions = getOptions('vehicle_use').filter(
    (opt) =>
      opt.value === '' ||
      ALLOWED_VEHICLE_USE_LABELS.includes(opt.label.toLowerCase()),
  );

  const vehicleEntity = vehicle as unknown as Record<string, unknown>;
  const isRequired = (field: string) =>
    isFieldRequired('vehicle', field, vehicleEntity);

  const vehicleUseLabel = getLabel(
    vehicle.vehicle_use || '',
    'vehicle_use',
  ).toLowerCase();
  const isCommuting = vehicleUseLabel === 'commuting';
  const isPleasure = vehicleUseLabel === 'pleasure';
  const oneWayKm = vehicle.distance.one_way_km;

  const oneWayKmSchemaError = validateField(
    'vehicle',
    'distance.one_way_km',
    vehicleEntity,
  );
  const annualKmSchemaError = validateField(
    'vehicle',
    'distance.annual_km',
    vehicleEntity,
  );
  const getOneWayKmError = () => {
    if (oneWayKmSchemaError) return oneWayKmSchemaError;
    if (isCommuting && oneWayKm === 0) {
      return 'Commute distance must be greater than 0';
    }
    return null;
  };
  const oneWayKmError = getOneWayKmError();

  const annualKmError = annualKmSchemaError;

  return (
    <Grid
      templateColumns={{
        base: '1fr',
        xl: 'repeat(3, 1fr)',
      }}
      gap={4}
      mb={4}
    >
      {/* Use */}
      <GridItem>
        <SectionCard title="Use" h="100%">
          <FormControl
            isRequired={isRequired('vehicle_use')}
            isInvalid={isRequired('vehicle_use') && !vehicle.vehicle_use}
          >
            <FormLabel fontSize="sm">Primary Use</FormLabel>
            <Select
              variant="main"
              size="sm"
              value={getCode(vehicle.vehicle_use, 'vehicle_use')}
              onChange={(e) =>
                onVehicleChange(index, 'vehicle_use', e.target.value)
              }
              isDisabled={false}
            >
              {vehicleUseOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </FormControl>
        </SectionCard>
      </GridItem>

      {/* Distance Driven */}
      <GridItem>
        <SectionCard title="Distance Driven" h="100%">
          <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={2}>
            <FormControl
              isRequired={!isPleasure && isRequired('distance.one_way_km')}
              isInvalid={!isPleasure && !!oneWayKmError}
              isDisabled={isPleasure}
            >
              <FormLabel fontSize="xs" whiteSpace="nowrap">
                1-Way
              </FormLabel>
              <Input
                variant="main"
                size="sm"
                type="number"
                maxLength={3}
                value={vehicle.distance.one_way_km ?? ''}
                onChange={(e) => {
                  if (e.target.value.length > 3) return;
                  onVehicleChange(
                    index,
                    'distance',
                    e.target.value ? Number(e.target.value) : null,
                    'one_way_km',
                  );
                }}
                isDisabled={isPleasure}
              />
              {!isPleasure && oneWayKmError && (
                <FormErrorMessage fontSize="xs">
                  {oneWayKmError}
                </FormErrorMessage>
              )}
            </FormControl>
            <FormControl
              isRequired={isRequired('distance.annual_km')}
              isInvalid={!!annualKmError}
            >
              <FormLabel fontSize="xs" whiteSpace="nowrap">
                Annually
              </FormLabel>
              <Input
                variant="main"
                size="sm"
                type="number"
                maxLength={6}
                value={vehicle.distance.annual_km ?? ''}
                onChange={(e) => {
                  if (e.target.value.length > 6) return;
                  onVehicleChange(
                    index,
                    'distance',
                    e.target.value ? Number(e.target.value) : null,
                    'annual_km',
                  );
                }}
                isDisabled={false}
              />
              {annualKmError && (
                <FormErrorMessage fontSize="xs">
                  {annualKmError}
                </FormErrorMessage>
              )}
            </FormControl>
            <ValidatedField
              itemPath="vehicle"
              fieldPath="distance.business_km"
              label="Business/Year"
              entity={vehicleEntity}
              labelSize="xs"
            >
              <Input
                variant="main"
                size="sm"
                type="number"
                maxLength={6}
                value={vehicle.distance.business_km ?? ''}
                onChange={(e) => {
                  if (e.target.value.length > 6) return;
                  onVehicleChange(
                    index,
                    'distance',
                    e.target.value ? Number(e.target.value) : null,
                    'business_km',
                  );
                }}
                isDisabled={false}
              />
            </ValidatedField>
          </SimpleGrid>
        </SectionCard>
      </GridItem>

      {/* Value */}
      <GridItem>
        <SectionCard title="Value" h="100%">
          <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2}>
            <ValidatedField
              itemPath="vehicle"
              fieldPath="value.list_price_new"
              label="List Price New"
              isDisabled={false}
              labelSize="xs"
            >
              <Input
                variant="main"
                size="sm"
                type="number"
                value={vehicle.value.list_price_new ?? ''}
                onChange={(e) =>
                  onVehicleChange(
                    index,
                    'value',
                    e.target.value ? Number(e.target.value) : null,
                    'list_price_new',
                  )
                }
                isDisabled={false}
              />
            </ValidatedField>
            <ValidatedField
              itemPath="vehicle"
              fieldPath="value.purchase_price"
              label="Purchase Price"
              isDisabled={false}
              labelSize="xs"
            >
              <Input
                variant="main"
                size="sm"
                type="number"
                value={vehicle.value.purchase_price ?? ''}
                onChange={(e) =>
                  onVehicleChange(
                    index,
                    'value',
                    e.target.value ? Number(e.target.value) : null,
                    'purchase_price',
                  )
                }
                isDisabled={false}
              />
            </ValidatedField>
          </SimpleGrid>
        </SectionCard>
      </GridItem>
    </Grid>
  );
};

export default VehicleUseDistanceValue;
